/**
 * Clientseitige HTML-Bereinigung für Editor-Fragmente (Textbausteine).
 *
 * Spiegelt die serverseitige Positivliste (backend/internal/textsnippets/
 * sanitize.go): erlaubt sind genau die Formatierungen, die der Editor
 * (MessageComposer.vue) selbst erzeugt. Alles andere – insbesondere
 * Bilder, Skripte, Frames, SVG und on*-Attribute – wird entfernt, bevor
 * ein Fragment per insertHTML in den Editor gelangt.
 *
 * Die Verarbeitung läuft über DOMParser: Das geparste Dokument ist inert,
 * es werden weder Skripte ausgeführt noch Ressourcen geladen.
 */

const ALLOWED_TAGS = new Set([
  'B', 'STRONG', 'I', 'EM', 'U', 'S', 'STRIKE',
  'UL', 'OL', 'LI', 'BR', 'P', 'DIV', 'SPAN', 'FONT', 'A',
])

/** Elemente, deren Inhalt mit entfernt wird (kein Text-Fallback). */
const DROP_WITH_CONTENT = new Set([
  'SCRIPT', 'STYLE', 'IFRAME', 'FRAME', 'FRAMESET', 'OBJECT', 'EMBED',
  'SVG', 'MATH', 'TEMPLATE', 'NOSCRIPT', 'TITLE', 'HEAD', 'LINK', 'META',
])

const ALLOWED_STYLES = new Set([
  'color', 'background-color', 'font-family', 'font-size', 'text-align',
])

const FONT_ATTR_PATTERNS: Record<string, RegExp> = {
  face: /^[\p{L}\p{N} ,'"-]+$/u,
  size: /^[1-7]$/,
  color: /^(#[0-9a-fA-F]{3,8}|[a-zA-Z]+|rgba?\([\d\s,.%]+\))$/,
}

const ALLOWED_HREF = /^(https?:|mailto:)/i

/** Style-Werte, die Ressourcen laden oder Code auswerten könnten. */
const UNSAFE_STYLE_VALUE = /url\s*\(|expression\s*\(|javascript:|@import|[<>\\]/i

export function sanitizeHtml (html: string): string {
  if (!html) return ''
  if (typeof DOMParser === 'undefined') return ''
  const doc = new DOMParser().parseFromString(html, 'text/html')
  cleanChildren(doc.body)
  return doc.body.innerHTML
}

/** Sichtbarer Text eines HTML-Fragments – ohne verbundenes DOM-Element. */
export function htmlToPlainText (html: string): string {
  if (!html) return ''
  if (typeof DOMParser === 'undefined') return ''
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim()
}

function cleanChildren (parent: Node): void {
  for (const child of Array.from(parent.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) continue
    if (child.nodeType !== Node.ELEMENT_NODE) {
      child.remove()
      continue
    }
    const el = child as Element
    if (DROP_WITH_CONTENT.has(el.tagName)) {
      el.remove()
      continue
    }
    if (!ALLOWED_TAGS.has(el.tagName)) {
      // Nicht erlaubtes Element entpacken: Kinder bleiben (bereinigt) erhalten.
      cleanChildren(el)
      while (el.firstChild) parent.insertBefore(el.firstChild, el)
      el.remove()
      continue
    }
    cleanAttributes(el)
    cleanChildren(el)
  }
}

function cleanAttributes (el: Element): void {
  for (const attr of Array.from(el.attributes)) {
    const name = attr.name.toLowerCase()
    const value = attr.value.trim()
    if (name === 'style') {
      const cleaned = cleanStyle(value)
      if (cleaned) el.setAttribute('style', cleaned)
      else el.removeAttribute(attr.name)
      continue
    }
    if (el.tagName === 'FONT' && FONT_ATTR_PATTERNS[name]?.test(value)) continue
    if (el.tagName === 'A' && name === 'href' && ALLOWED_HREF.test(value)) {
      el.setAttribute('rel', 'nofollow noopener')
      continue
    }
    if (el.tagName === 'A' && name === 'rel') continue
    el.removeAttribute(attr.name)
  }
}

function cleanStyle (style: string): string {
  const kept: string[] = []
  for (const decl of style.split(';')) {
    const idx = decl.indexOf(':')
    if (idx < 0) continue
    const prop = decl.slice(0, idx).trim().toLowerCase()
    const value = decl.slice(idx + 1).trim()
    if (!ALLOWED_STYLES.has(prop) || !value || UNSAFE_STYLE_VALUE.test(value)) continue
    kept.push(`${prop}: ${value}`)
  }
  return kept.join('; ')
}
